import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from '@/shared/types';
import { ApiClientError, normalizeApiError } from '@/lib/api-error';
import { shouldBlockForInvalidSession } from '@/lib/auth-request-policy';
import { getLocaleHeaders, i18n } from '@/i18n';
import { toast } from '@/shared/feedback/toast';
import { CSRF_COOKIE_NAME } from '@/shared/auth/cookies';
import {
  finishRequestProgress,
  startRequestProgress,
} from '@/shared/api/request-progress';

type RetriableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _progressStarted?: boolean;
  skipProgressBar?: boolean;
};

type AuthSessionAdapter = {
  getAccessToken: () => string | null;
  setTokens: (tokens: { accessToken: string; expiresIn: number }) => void;
  setUser: (user: unknown) => void;
  setTenant?: (tenant: unknown | null) => void;
  setPermissions: (permissions: { menus: string[]; features: Record<string, boolean> }) => void;
  clearStoredAuth: () => void;
};

type ApiNavigationAdapter = {
  getCurrentRouteName: () => unknown;
  getCurrentFullPath: () => string;
  push: (to: { name: string; query?: Record<string, string> }) => Promise<unknown>;
};

let authSessionAdapter: AuthSessionAdapter | null = null;
let apiNavigationAdapter: ApiNavigationAdapter | null = null;

export function configureApiAuthSession(adapter: AuthSessionAdapter) {
  authSessionAdapter = adapter;
}

export function configureApiNavigation(adapter: ApiNavigationAdapter) {
  apiNavigationAdapter = adapter;
}

export const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const ACTIVE_WORKSPACE_STORAGE_KEY = 'tenant.activeWorkspaceId';

function readCookie(name: string) {
  const prefix = `${encodeURIComponent(name)}=`;
  const entry = document.cookie
    .split('; ')
    .find((value) => value.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
}

let refreshPromise: Promise<string | null | 'session_revoked'> | null = null;
let redirectingToLogin = false;
let authSessionInvalid = false;

export function resetAuthSessionState() {
  authSessionInvalid = false;
  redirectingToLogin = false;
  refreshPromise = null;
}

export async function refreshAccessToken(): Promise<string | null | 'session_revoked'> {
  if (!refreshPromise) {
    startRequestProgress();
    refreshPromise = axios
      .post<ApiResponse<{
        accessToken: string;
        user?: any;
        tenant?: any;
        permissions?: { menus: string[]; features: Record<string, boolean> };
      }>>(
        '/api/v1/auth/refresh',
        {},
        {
          withCredentials: true,
          headers: { 'x-csrf-token': readCookie('saas_platform_csrf') },
        },
      )
      .then((response) => {
        if (!response.data.success || !response.data.data) {
          return null;
        }
        authSessionAdapter?.setTokens({
          accessToken: response.data.data.accessToken,
          expiresIn: 0,
        });
        if (response.data.data.user) authSessionAdapter?.setUser(response.data.data.user);
        if (response.data.data.tenant !== undefined) {
          authSessionAdapter?.setTenant?.(response.data.data.tenant);
        }
        if (response.data.data.permissions) {
          authSessionAdapter?.setPermissions(response.data.data.permissions);
        }
        return response.data.data.accessToken;
      })
      .catch((error: AxiosError<ApiResponse<any>>) => {
        if (error.response?.data?.code === 'SESSION_REVOKED') {
          return 'session_revoked';
        }
        return null;
      })
      .finally(() => {
        finishRequestProgress();
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isSessionRevokedResponse(error?: AxiosError<ApiResponse<any>>) {
  return error?.response?.data?.code === 'SESSION_REVOKED';
}

async function revokeBrowserSession() {
  try {
    await axios.post(
      '/api/v1/auth/session/revoke',
      {},
      {
        withCredentials: true,
        headers: { 'x-csrf-token': readCookie(CSRF_COOKIE_NAME) },
      },
    );
  } catch {
    // Best effort: local state is still cleared below, and stale cookies will fail server validation.
  }
}

async function forceLogout(options: { reason?: 'session_expired' | 'session_revoked' } = {}) {
  authSessionInvalid = true;
  await revokeBrowserSession();
  authSessionAdapter?.clearStoredAuth();
  const message = options.reason === 'session_revoked'
    ? i18n.global.t('auth.sessionRevoked')
    : i18n.global.t('auth.sessionExpired');

  if (
    apiNavigationAdapter &&
    !redirectingToLogin &&
    apiNavigationAdapter.getCurrentRouteName() !== 'Login'
  ) {
    redirectingToLogin = true;
    const query: Record<string, string> = {};
    if (options.reason === 'session_revoked') {
      query.reason = 'session_revoked';
    } else {
      query.reason = 'session_expired';
      const redirect = apiNavigationAdapter.getCurrentFullPath();
      if (redirect.startsWith('/') && redirect !== '/login') {
        query.redirect = redirect;
      }
    }
    await apiNavigationAdapter.push({
      name: 'Login',
      query: Object.keys(query).length > 0 ? query : undefined,
    });
    redirectingToLogin = false;
  }

  toast.warning(message);

  return Promise.reject(new ApiClientError(message, {
    code: options.reason === 'session_revoked' ? 'SESSION_REVOKED' : 'UNAUTHORIZED',
    status: 401,
  }));
}

async function handleUnauthorized(
  config?: RetriableConfig,
  error?: AxiosError<ApiResponse<any>>,
) {
  const sessionRevoked = isSessionRevokedResponse(error);

  if (config?.url?.includes('/auth/refresh')) {
    return forceLogout(sessionRevoked ? { reason: 'session_revoked' } : {});
  }

  if (!sessionRevoked && config && !config._retry && !authSessionInvalid) {
    const nextToken = await refreshAccessToken();
    if (nextToken === 'session_revoked') {
      return forceLogout({ reason: 'session_revoked' });
    }
    if (nextToken && config.headers) {
      config._retry = true;
      config.headers.Authorization = `Bearer ${nextToken}`;
      return api(config);
    }
  }

  return forceLogout(sessionRevoked ? { reason: 'session_revoked' } : {});
}

api.interceptors.request.use(
  (config) => {
    const isRefreshRequest = config.url?.includes('/auth/refresh');

    if (
      authSessionInvalid &&
      !isRefreshRequest &&
      shouldBlockForInvalidSession(config.url)
    ) {
      return Promise.reject(new Error(i18n.global.t('errors.unauthorized')));
    }

    const accessToken = authSessionAdapter?.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    const activeWorkspaceId =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
        : null;
    if (activeWorkspaceId && !config.headers['x-workspace-id']) {
      config.headers['x-workspace-id'] = activeWorkspaceId;
    }
    Object.assign(config.headers, getLocaleHeaders());
    const csrfToken = readCookie('saas_platform_csrf');
    if (
      csrfToken &&
      !['get', 'head', 'options'].includes(
        (config.method || 'get').toLowerCase(),
      )
    ) {
      config.headers['x-csrf-token'] = csrfToken;
    }

    const retriableConfig = config as RetriableConfig;
    if (!retriableConfig.skipProgressBar) {
      startRequestProgress();
      retriableConfig._progressStarted = true;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

function finishTrackedProgress(config?: InternalAxiosRequestConfig) {
  const retriableConfig = config as RetriableConfig | undefined;
  if (retriableConfig?._progressStarted) {
    finishRequestProgress();
    retriableConfig._progressStarted = false;
  }
}

api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<any>>) => {
    finishTrackedProgress(response.config);
    if (response.data.success === false) {
      return Promise.reject(
        normalizeApiError(response, i18n.global.t('errors.requestFailed')),
      );
    }
    return response;
  },
  async (error: AxiosError<ApiResponse<any>>) => {
    finishTrackedProgress(error.config);
    if (error.response?.status === 401) {
      const config = error.config as RetriableConfig | undefined;
      const isRefreshRequest = config?.url?.includes('/auth/refresh');
      const shouldTreatAsSessionExpiry = isRefreshRequest || shouldBlockForInvalidSession(config?.url);

      if (!shouldTreatAsSessionExpiry) {
        return Promise.reject(normalizeApiError(error));
      }

      return handleUnauthorized(config, error);
    }

    return Promise.reject(normalizeApiError(error));
  },
);

export default api;
